import { FLOTABOT } from "../components/layout/secciones";
import { abrirSesionEnFlotaBot } from "./ssoFlotaBot";

/* Mandar la foto del registro a que la lean.
 *
 * ── POR QUÉ NO SE LEE ACÁ ─────────────────────────────────────────────────
 *
 * El hub es React estático: no tiene servidor, así que no tiene con qué llamar
 * al modelo — y la clave de la API no puede vivir en el navegador. FlotaBot ya
 * tiene las dos cosas y el molde entero (`vale_foto.py`). Mismo camino que
 * `sincronizarDocs`: SSO antes de cada llamada, `credentials: "include"`, y un
 * 200 que no es JSON NO es un éxito.
 *
 * ── Y LA RUTA NO ESCRIBE NADA ─────────────────────────────────────────────
 *
 * Devuelve lo que entendió y se termina ahí. Guarda el hub, con
 * `corregirDocumento`, que es el cuello donde se sella quién lo hizo — si
 * escribiera FlotaBot, la fila quedaría firmada por el servidor y una fecha mal
 * leída no tendría a quién preguntarle.
 */

/* Ocho megas, el mismo tope que el servidor. Se chequea de los dos lados a
 * propósito: acá para no hacer esperar veinte segundos a alguien por un archivo
 * que va a rebotar, y allá porque el chequeo del navegador es comodidad y no
 * una pared. */
export const TOPE_BYTES = 8 * 1024 * 1024;

/* Lo que la API de Anthropic acepta. Un HEIC de iPhone entra por acá y hay que
 * decirlo con esas palabras: «formato no soportado» manda a buscar el problema
 * a la foto, y lo que hay que hacer es sacarla o exportarla como JPG.
 *
 * EL PDF ESTÁ ACÁ PORQUE UN ESCÁNER NO SACA FOTOS. Marcos escanea, y lo que
 * sale de un escáner es un PDF: pedirle que lo convierta a JPG para poder
 * soltarlo es pedirle un paso que se hace una vez y después no se hace más.
 * Del lado del servidor va por otro camino (bloque `document` y no `image`),
 * pero eso acá no cambia nada: es un archivo que se puede leer. */
export const FORMATOS = [
  "image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf",
];

export function revisarArchivo(archivo) {
  if (!archivo) return "No llegó ningún archivo.";
  if (archivo.size > TOPE_BYTES) {
    return "El archivo pesa más de 8 MB. Escaneá con menos resolución o " +
      "sacá la foto más chica.";
  }
  if (!FORMATOS.includes(archivo.type)) {
    /* El tipo vacío pasa: algunos navegadores no lo completan al arrastrar, y
     * rechazar por eso sería frenar una foto que se puede leer perfectamente.
     * Si de verdad no se puede, lo dice el servidor. */
    if (archivo.type) {
      return `No puedo leer un ${archivo.type}. Tiene que ser JPG, PNG, WEBP o PDF` +
        (archivo.type === "image/heic" || archivo.type === "image/heif"
          ? " — un HEIC de iPhone se exporta como JPG desde la misma app de Fotos."
          : ".");
    }
  }
  return null;
}

/* `{ ok, mensaje?, lectura? }`. Nunca lanza. */
export async function leerRegistro(archivo) {
  const malo = revisarArchivo(archivo);
  if (malo) return { ok: false, mensaje: malo };

  const sesion = await abrirSesionEnFlotaBot();
  if (!sesion.ok) {
    return {
      ok: false,
      mensaje:
        sesion.motivo === "sin-sesion"
          ? "Se cerró la sesión. Entrá de nuevo y probá otra vez."
          : "No se pudo hablar con FlotaBot. Puede estar reiniciando; probá en un minuto.",
    };
  }

  const cuerpo = new FormData();
  cuerpo.append("foto", archivo);

  let r;
  try {
    r = await fetch(`${FLOTABOT}/api/leer-registro`, {
      method: "POST",
      credentials: "include",
      body: cuerpo,
    });
  } catch {
    // Red o CORS. Para el que soltó la foto son lo mismo —no pasó nada— y
    // nombrar CORS lo manda a buscar donde no lo puede arreglar.
    return { ok: false, mensaje: "No se pudo conectar con FlotaBot." };
  }

  /* «No podés» y «no pude» no son lo mismo. */
  if (r.status === 403) {
    return {
      ok: false,
      mensaje: "No podés leer el registro.",
    };
  }
  if (r.status === 401) {
    return {
      ok: false,
      mensaje: "FlotaBot no reconoció la sesión. Recargá la página y probá otra vez.",
    };
  }

  let json = null;
  try {
    json = await r.json();
  } catch {
    json = null;
  }
  /* Un 200 con HTML adentro es lo que devolvía la ruta antes de tener CORS: el
   * fetch seguía el redirect al login y traía la página con 200. Sin este
   * chequeo, la pantalla diría «lo leí» sin haber leído nada. */
  if (!json) return { ok: false, mensaje: `FlotaBot contestó ${r.status}.` };
  if (!json.ok) {
    return { ok: false, mensaje: json.error || "No se pudo leer la imagen." };
  }
  return { ok: true, lectura: json };
}
